import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios from "helpers/apiAxios";
import { handleAsyncThunkError } from "redux/reduxHelpers";

// Build base URL for any recipient (contact or company)
// e.g. /crm/contacts/42/activities  or  /crm/companies/7/tasks
const base = (recipientType, recipientId, resource) => {
  const prefix = recipientType === 'contact' ? 'contacts' : 'companies';
  return `/crm/${prefix}/${recipientId}/${resource}`;
};

const buildFormData = (ns, data, files) => {
  const fd = new FormData();
  Object.entries(data).forEach(([k, v]) => {
    if (v !== null && v !== undefined) fd.append(`${ns}[${k}]`, v);
  });
  if (files?.length) files.forEach(f => fd.append(`${ns}[attachments][]`, f));
  return fd;
};

// ── Activities ────────────────────────────────────────────────────────────────
export const getRecipientActivities = createAsyncThunk(
  "RecipientActivity/getActivities",
  async ({ recipientType, recipientId, page = 1, perPage = 20, activityType, q, activeOnly }, { rejectWithValue }) => {
    try {
      const params = { page, per_page: perPage };
      if (activityType)       params.activity_type = activityType;
      if (q)                  params.q = q;
      // activeOnly === false → send nothing, the backend returns all
      if (activeOnly !== false) params.active_only = 'true';
      const res = await apiAxios.get(base(recipientType, recipientId, 'activities'), { params });
      return { ...res.data, page };
    } catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to load activities")); }
  }
);

export const createRecipientActivity = createAsyncThunk(
  "RecipientActivity/createActivity",
  async ({ recipientType, recipientId, data, files }, { rejectWithValue }) => {
    try {
      const payload = files?.length ? buildFormData('activity', data, files) : { activity: data };
      const cfg     = files?.length ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
      const res = await apiAxios.post(base(recipientType, recipientId, 'activities'), payload, cfg);
      return res.data;
    } catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to create activity")); }
  }
);

export const updateRecipientActivity = createAsyncThunk(
  "RecipientActivity/updateActivity",
  async ({ recipientType, recipientId, id, data, files }, { rejectWithValue }) => {
    try {
      const payload = files?.length ? buildFormData('activity', data, files) : { activity: data };
      const cfg     = files?.length ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
      const res = await apiAxios.put(`${base(recipientType, recipientId, 'activities')}/${id}`, payload, cfg);
      return res.data;
    } catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to update activity")); }
  }
);

export const deleteRecipientActivity = createAsyncThunk(
  "RecipientActivity/deleteActivity",
  async ({ recipientType, recipientId, id }, { rejectWithValue }) => {
    try {
      await apiAxios.delete(`${base(recipientType, recipientId, 'activities')}/${id}`);
      return id;
    } catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to delete activity")); }
  }
);

// ── Tasks ─────────────────────────────────────────────────────────────────────
export const getRecipientTasks = createAsyncThunk(
  "RecipientActivity/getTasks",
  async ({ recipientType, recipientId, page = 1, perPage = 20, q, activeOnly }, { rejectWithValue }) => {
    try {
      const params = { page, per_page: perPage };
      if (q) params.q = q;
      if (activeOnly !== false) params.active_only = 'true';
      const res = await apiAxios.get(base(recipientType, recipientId, 'tasks'), { params });
      return { ...res.data, page };
    } catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to load tasks")); }
  }
);

export const createRecipientTask = createAsyncThunk(
  "RecipientActivity/createTask",
  async ({ recipientType, recipientId, data, files }, { rejectWithValue }) => {
    try {
      const payload = files?.length ? buildFormData('task', data, files) : { task: data };
      const cfg     = files?.length ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
      const res = await apiAxios.post(base(recipientType, recipientId, 'tasks'), payload, cfg);
      return res.data;
    } catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to create task")); }
  }
);

export const updateRecipientTask = createAsyncThunk(
  "RecipientActivity/updateTask",
  async ({ recipientType, recipientId, id, data, files }, { rejectWithValue }) => {
    try {
      const payload = files?.length ? buildFormData('task', data, files) : { task: data };
      const cfg     = files?.length ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
      const res = await apiAxios.put(`${base(recipientType, recipientId, 'tasks')}/${id}`, payload, cfg);
      return res.data;
    } catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to update task")); }
  }
);

export const deleteRecipientTask = createAsyncThunk(
  "RecipientActivity/deleteTask",
  async ({ recipientType, recipientId, id }, { rejectWithValue }) => {
    try {
      await apiAxios.delete(`${base(recipientType, recipientId, 'tasks')}/${id}`);
      return id;
    } catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to delete task")); }
  }
);

// ── Notes ─────────────────────────────────────────────────────────────────────
export const getRecipientNotes = createAsyncThunk(
  "RecipientActivity/getNotes",
  async ({ recipientType, recipientId, page = 1, perPage = 15, q }, { rejectWithValue }) => {
    try {
      const params = { page, per_page: perPage };
      if (q) params.q = q;
      const res = await apiAxios.get(base(recipientType, recipientId, 'notes'), { params });
      return { ...res.data, page };
    } catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to load notes")); }
  }
);

export const createRecipientNote = createAsyncThunk(
  "RecipientActivity/createNote",
  async ({ recipientType, recipientId, content }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(base(recipientType, recipientId, 'notes'), { note: { content } });
      return res.data;
    } catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to create note")); }
  }
);

export const updateRecipientNote = createAsyncThunk(
  "RecipientActivity/updateNote",
  async ({ recipientType, recipientId, id, content }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.put(`${base(recipientType, recipientId, 'notes')}/${id}`, { note: { content } });
      return res.data;
    } catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to update note")); }
  }
);

export const deleteRecipientNote = createAsyncThunk(
  "RecipientActivity/deleteNote",
  async ({ recipientType, recipientId, id }, { rejectWithValue }) => {
    try {
      await apiAxios.delete(`${base(recipientType, recipientId, 'notes')}/${id}`);
      return id;
    } catch (e) { return rejectWithValue(handleAsyncThunkError(e, "Failed to delete note")); }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────
const paged = () => ({ data: [], meta: null, loading: false, loadingMore: false });

const upsert = (arr, item) => {
  const idx = arr.findIndex(x => x.id === item.id);
  return idx >= 0 ? arr.map((x, i) => i === idx ? item : x) : [item, ...arr];
};

const applyPaged = (slice, payload) => {
  slice.loading = slice.loadingMore = false;
  slice.data = payload.page === 1
    ? (payload.data || [])
    : [...slice.data, ...(payload.data || [])];
  slice.meta = payload.meta || null;
};

const recipientActivitySlice = createSlice({
  name: "crm_recipient_activity",
  initialState: {
    activities: paged(), tasks: paged(), notes: paged(),
    submitting: false, error: null,
  },
  reducers: {
    resetRecipientActivity: () => ({
      activities: paged(), tasks: paged(), notes: paged(),
      submitting: false, error: null,
    }),
  },
  extraReducers: (builder) => {
    builder
      // Activities
      .addCase(getRecipientActivities.pending, (s, a) => {
        if (!a.meta.arg.page || a.meta.arg.page === 1) s.activities.loading = true;
        else s.activities.loadingMore = true;
      })
      .addCase(getRecipientActivities.fulfilled, (s, a) => { applyPaged(s.activities, a.payload); })
      .addCase(getRecipientActivities.rejected,  s => { s.activities.loading = s.activities.loadingMore = false; })
      .addCase(createRecipientActivity.pending,   s => { s.submitting = true; })
      .addCase(createRecipientActivity.fulfilled, (s, a) => { s.submitting = false; s.activities.data = [a.payload.data, ...s.activities.data]; })
      .addCase(createRecipientActivity.rejected,  (s, a) => { s.submitting = false; s.error = a.payload; })
      .addCase(updateRecipientActivity.fulfilled, (s, a) => { s.activities.data = upsert(s.activities.data, a.payload.data); })
      .addCase(deleteRecipientActivity.fulfilled, (s, a) => { s.activities.data = s.activities.data.filter(x => x.id !== a.payload); })
      // Tasks
      .addCase(getRecipientTasks.pending, (s, a) => {
        if (!a.meta.arg.page || a.meta.arg.page === 1) s.tasks.loading = true;
        else s.tasks.loadingMore = true;
      })
      .addCase(getRecipientTasks.fulfilled, (s, a) => { applyPaged(s.tasks, a.payload); })
      .addCase(getRecipientTasks.rejected,  s => { s.tasks.loading = s.tasks.loadingMore = false; })
      .addCase(createRecipientTask.pending,   s => { s.submitting = true; })
      .addCase(createRecipientTask.fulfilled, (s, a) => { s.submitting = false; s.tasks.data = [a.payload.data, ...s.tasks.data]; })
      .addCase(createRecipientTask.rejected,  (s, a) => { s.submitting = false; s.error = a.payload; })
      .addCase(updateRecipientTask.fulfilled, (s, a) => { s.tasks.data = upsert(s.tasks.data, a.payload.data); })
      .addCase(deleteRecipientTask.fulfilled, (s, a) => { s.tasks.data = s.tasks.data.filter(x => x.id !== a.payload); })
      // Notes
      .addCase(getRecipientNotes.pending, (s, a) => {
        if (!a.meta.arg.page || a.meta.arg.page === 1) s.notes.loading = true;
        else s.notes.loadingMore = true;
      })
      .addCase(getRecipientNotes.fulfilled, (s, a) => { applyPaged(s.notes, a.payload); })
      .addCase(getRecipientNotes.rejected,  s => { s.notes.loading = s.notes.loadingMore = false; })
      .addCase(createRecipientNote.pending,   s => { s.submitting = true; })
      .addCase(createRecipientNote.fulfilled, (s, a) => { s.submitting = false; s.notes.data = [a.payload.data, ...s.notes.data]; })
      .addCase(createRecipientNote.rejected,  (s, a) => { s.submitting = false; s.error = a.payload; })
      .addCase(updateRecipientNote.fulfilled, (s, a) => { s.notes.data = upsert(s.notes.data, a.payload.data); })
      .addCase(deleteRecipientNote.fulfilled, (s, a) => { s.notes.data = s.notes.data.filter(x => x.id !== a.payload); });
  },
});

export const { resetRecipientActivity } = recipientActivitySlice.actions;
export default recipientActivitySlice.reducer;
